import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cachedDrivers, fetchDrivers, type Driver } from '../../lib/driversApi';
import { pickName } from './helpers';
import type { KdsTicket } from '../../types/kds';

// ─────────────────────────────────────────────────────────────────────────────
// Handing a DELIVERY order over at the pass.
//
// The bag leaving the kitchen and the driver taking it are one moment, not two.
// The cook is standing there when it happens and the driver is standing in
// front of them — so the question gets asked here, on the same tap that clears
// the card, rather than being left for whoever next opens the till.
//
// This is deliberately the same shape as the call centre's DispatchDialog, down
// to the two-verb button, because it is the same act performed by a different
// pair of hands. Anyone who has used one should recognise the other.
//
// THE DRIVER IS OPTIONAL, and the confirm button is NEVER disabled for the want
// of one. Plenty of kitchens hand the bag to whoever is free, and a tenant that
// has not set its drivers up at all must still be able to clear its board — if
// it could not, the only way out would be to name somebody who did not take it.
// The button says which of the two hand-overs it is about to do, so an empty
// picker reads as a choice rather than as an unfinished form.
//
// It only ever opens for order type 3 (delivery). Dine-in, takeaway and pickup
// serve on one tap exactly as before — asking a cook who took a plate to a table
// would be a question with no answer.
// ─────────────────────────────────────────────────────────────────────────────

export interface ServeDialogProps {
  ticket: KdsTicket;
  onCancel: () => void;
  /** Confirm. `driverId` is null for "hand over, nobody named". */
  onConfirm: (driverId: number | null) => void;
}

export function ServeDialog({ ticket, onCancel, onConfirm }: ServeDialogProps): JSX.Element {
  const { lang, t } = useI18n();
  // Seeded from the module cache so a second hand-over in the same service
  // paints the list on the first frame instead of flashing a spinner.
  const [drivers, setDrivers] = useState<Driver[] | null>(() => cachedDrivers());
  const [failed, setFailed] = useState(false);
  const [driverId, setDriverId] = useState<number | null>(null);
  const [filter, setFilter] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    if (cachedDrivers() && attempt === 0) return undefined;
    setFailed(false);
    fetchDrivers()
      .then((rows) => {
        if (live) setDrivers(rows);
      })
      .catch(() => {
        // A driver list that will not load must not trap the cook with a bag in
        // their hand: the dialog stays usable and "no driver" still hands over.
        if (live) {
          setDrivers([]);
          setFailed(true);
        }
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  // Escape cancels, Enter confirms whatever the button currently says. A pass
  // often has a keyboard or a bump bar wired to one, and reaching for the mouse
  // mid-service is the thing this screen exists to avoid.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCancel();
      }
      if (event.key === 'Enter') {
        const target = event.target as HTMLElement | null;
        if (target && target.tagName === 'INPUT') return; // the search box
        event.preventDefault();
        event.stopPropagation();
        onConfirm(driverId);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [driverId, onCancel, onConfirm]);

  const list = drivers ?? [];
  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((d) =>
      `${d.name ?? ''} ${d.name_ar ?? ''} ${d.phone ?? ''}`.toLowerCase().includes(needle),
    );
  }, [list, filter]);

  const customer = pickName(lang, ticket.customerName, ticket.customerNameAr);

  return (
    <div className="kds-modal-backdrop" onClick={onCancel} role="presentation">
      <div
        className="kds-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('handOver')}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="kds-modal-head">
          <div>
            <div className="kds-modal-title">{t('handOver')}</div>
            <div className="kds-modal-sub">
              <bdi>{ticket.orderCode ?? `#${ticket.orderId}`}</bdi>
              {customer !== '—' && <> · {customer}</>}
            </div>
          </div>
          <button
            type="button"
            className="kds-modal-close"
            onClick={onCancel}
            aria-label={t('cancel')}
          >
            ×
          </button>
        </header>

        <div className="kds-modal-body">
          <div className="kds-modal-label">{t('driverOptional')}</div>

          {drivers === null && <div className="kds-modal-note">{t('loadingDrivers')}</div>}

          {failed && (
            <div className="kds-modal-note">
              {t('driversFailed')}{' '}
              <button
                type="button"
                className="kds-linkish"
                onClick={() => setAttempt((n) => n + 1)}
              >
                {t('retry')}
              </button>
            </div>
          )}

          {drivers !== null && !failed && list.length === 0 && (
            <div className="kds-modal-note">{t('noDriversConfigured')}</div>
          )}

          {list.length > 0 && (
            <>
              {list.length > 6 && (
                <input
                  className="kds-modal-search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={t('searchDriver')}
                  aria-label={t('searchDriver')}
                />
              )}
              <ul className="kds-driver-list" role="listbox" aria-label={t('driver')}>
                {/* "Without a driver" is an option in the list, not just the
                    absence of one — a cook scanning for the fastest tap should
                    find it where they are already looking. */}
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={driverId === null}
                    className={`kds-driver ${driverId === null ? 'selected' : ''}`}
                    onClick={() => setDriverId(null)}
                  >
                    <span className="kds-driver-name">{t('noDriver')}</span>
                  </button>
                </li>
                {filtered.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={driverId === d.id}
                      className={`kds-driver ${driverId === d.id ? 'selected' : ''}`}
                      onClick={() => setDriverId(d.id)}
                    >
                      <span className="kds-driver-name">
                        {pickName(lang, d.name, d.name_ar)}
                      </span>
                      {d.phone && <span className="kds-driver-phone">{d.phone}</span>}
                    </button>
                  </li>
                ))}
                {filtered.length === 0 && (
                  <li className="kds-modal-note">{t('noMatches')}</li>
                )}
              </ul>
            </>
          )}
        </div>

        <footer className="kds-modal-foot">
          <p className="kds-modal-fine">{t('deliveryNoMoney')}</p>
          <div className="kds-modal-actions">
            <button type="button" className="kds-btn" onClick={onCancel}>
              {t('cancel')}
            </button>
            <button
              type="button"
              className="kds-btn kds-btn-primary"
              onClick={() => onConfirm(driverId)}
            >
              {driverId == null ? t('serveNoDriver') : t('serveWithDriver')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
